import React, {useEffect, useMemo, useRef, useState} from 'react'
import ReactDOM from "react-dom";
import {Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import {useMutation} from "@tanstack/react-query";
import Client from "../../models/Client";
import SupplierKeyClientsStep from "./components/SupplierKeyClientsStep";
import SupplierWorkGalleryStep from "./components/SupplierWorkGalleryStep";
import SupplierPartnerProgramsStep from "./components/SupplierPartnerProgramsStep";
import SupplierBrandsStep from "./components/SupplierBrandsStep";
import SupplierAwardsStep from "./components/SupplierAwardsStep";
import SupplierInfoStep from "./components/SupplierInfoStep";
import ServiceOfferingsStep from "./components/ServiceOfferingsStep";
import SupplierIndustriesStep from "./components/SupplierIndustriesStep";
import SupplierTalentResourcesStep from "./components/SupplierTalentResourcesStep";
import PageNotFound from "../../components/errors/PageNotFound";
import {useMount} from "react-use";
import {Helmet} from "react-helmet";
import {ArrowRightOutlined} from "@ant-design/icons";

const supplierUpdateRequest = async (clientId, params) => {
    let displayIndexCheck = [
        'brands',
        'industries_worked',
        'assets',
        'service_offerings',
        'key_clients',
        'partnerable_programs',
    ]

    let updateParams = TB.cloneDeep(params);

    TB.map(displayIndexCheck, prop => {
        if (updateParams[prop]?.length) {
            updateParams[prop] = TB.orderBy(updateParams[prop], ['display_index',item => TB.isDefined('display_index')], ['asc', 'desc']);
            updateParams[prop] = TB.map(updateParams[prop], (item, index) => {
                return {
                    ...item,
                    display_index: index+1
                }
            })
        }
    })

    if (updateParams.locations?.length) {
        updateParams.locations = [updateParams.location, ...updateParams.locations];
    }else if(updateParams.location) {
        updateParams.locations = [updateParams.location];
    }

    TB.auth().SupplierUpdateRequest = TB.request(
        `/api/clients/${clientId}/update`,
        updateParams
    ).headers({
        'Content-Type': 'multipart/form-data'
    });



    let response = await TB.auth().SupplierUpdateRequest.post().catch(e => {
        console.warn('ERROR: supplierUpdate', e);
    })

    if (!response || !TB.has(response, 'success.data.client') || !TB.has(response, 'success.data.auth')) {
        TB.auth().SupplierUpdateRequest.didHandleError = await TB.auth().SupplierUpdateRequest.handleError();
        if (TB.auth().SupplierUpdateRequest.didHandleError
            && TB.auth().SupplierUpdateRequest.shouldRerun) {
            return supplierUpdateRequest(clientId, params);
        }

        return false;
    }

    return response
}

const DEFAULT_REQUEST_PARAMS = [
    'name',
    'size',
    'website',
    'location',
    'locations',
    'is_black_owned',
    'is_woman_owned',
    'targets_clients_enterprise',
    'targets_clients_startup',
    'targets_clients_mid',
    'targets_clients_small',
    'approved_email_domains',
    'logo_path',
    'banner_image_path',
    'about',
    'offers_service_work',
    'offers_byo_work',
    'target_annual_fees',
    'target_project_budget',
    // 'service_offerings',
]
const SupplierManageProfile = (props) => {
    const [queryParams, setQueryParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const formRef = useRef(null);

    const {auth, update: updateAuth, appropriate, logout} = useAuth();

    const [isDisabled, setIsDisabled] = useState(false);

    const [stepSubmitDisabled, setStepSubmitDisabled] = useState(false);

    const [supplierOrg, setSupplierOrg] = useState(() => {
        return Client?.make(TB.cloneDeep({...auth.supplierOrg()}));
    });

    // Check if AI profile data exists and is not empty
    const hasAIProfileData = supplierOrg?.generated_account_data &&
        Object.keys(supplierOrg.generated_account_data).length > 0;

    const secondaryColor = TB.config('color.success-darker');

    // console.log(supplierOrg,' SUPPLIER ORG')


    const updateSupplierManageProfileMutation = useMutation(
        ['supplierProfileUpdate', supplierOrg.id],
        params => supplierUpdateRequest(supplierOrg.id, params)
    )

    const profileCompleted = useMemo(() => {
        return auth.supplierOrg?.()?.hasCompletedSupplierProfile()
    }, [isDisabled, updateSupplierManageProfileMutation?.isLoading]);


    const activeSteps = useMemo(() => {
        return [
            {
                path: 'about',
                label: 'Agency Info',
                isCompleted: profileCompleted,
                needsCompletion: !profileCompleted,
                validateInputs: (params) => {
                    if(!params?.has_set_target_client_size) {
                        TB.alert('error', 'Client Target Size Required','Please select at least one client target size.', [{
                            title: 'Ok'
                        }])
                        return false;
                    }
                    return true;
                },
                icon: 'fas fa-info-circle',
                requestParams: ['locations']
            },
            {
                path: 'brands',
                label: 'Brands',
                isCompleted: auth.supplierOrg?.()?.brands?.length > 0,
                needsCompletion: !auth.supplierOrg?.()?.brands?.length,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-handshake',
                requestParams: ['brands']
            },
            {
                path: 'project-highlights',
                label: 'Project Highlights',
                isCompleted: auth.supplierOrg?.()?.key_clients?.length > 0,
                needsCompletion: !auth.supplierOrg?.()?.key_clients?.length,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-briefcase',
                requestParams: ['key_clients']
            },
            {
                path: 'work-gallery',
                label: 'Work Gallery',
                isCompleted: true,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-images',
                requestParams: ['assets']
            },
            {
                path: 'partner-programs',
                label: 'Partner Programs',
                isCompleted: true,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-certificate',
                requestParams: ['partnerable_programs']
            },
            {
                path: 'industries',
                label: 'Industry Experience',
                isCompleted: true,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-building',
                requestParams: ['industries_worked']
            },
            {
                path: 'awards-certifications',
                label: 'Awards & Certifications',
                isCompleted: true,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-trophy',
                requestParams: ['awards', 'certifications']
            },
            {
                path: 'service-offerings',
                label: 'Service Offerings',
                isCompleted: auth.supplierOrg?.()?.service_offerings?.length > 0,
                needsCompletion: !auth.supplierOrg?.()?.service_offerings?.length,
                // isDisabled: !profileCompleted,
                icon: 'fas fa-cogs',
                requestParams: ['service_offerings',]
            },
            // {
            //     path: 'links-documents',
            //     label: 'Links & Documents',
            //     isCompleted: true,
            //     // isDisabled: !profileCompleted,
            //     icon: 'fas fa-link',
            //     requestParams: ['profile_links', 'profile_documents',]
            // },
            {
                path: 'talent-resources',
                label: 'Talent Resources',
                isCompleted: true,
                // isDisabled: !profileCompleted,
                needsCompletion: !auth.supplierOrg?.().total_supplied_talent_users,
                icon: 'fas fa-users'
            },
        ]
    }, [profileCompleted, isDisabled, updateSupplierManageProfileMutation?.isLoading,
        auth.supplierOrg?.()?.service_offerings?.length,
        auth.supplierOrg?.()?.brands?.length,
        auth.supplierOrg?.()?.key_clients?.length,
        auth.supplierOrg?.().total_supplied_talent_users]);

    const [lastSavedStep, setLastSavedStep] = useState(() => activeSteps[0])

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(activeSteps, (step, index) => {
            return location.pathname.includes(step.path)
        })
    }, [location?.pathname, activeSteps]);

    const currentStep = useMemo(() => {
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const nextStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex + 1]
        }
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const prevStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex - 1]
        }

        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    useEffect(() => {
        if (!supplierOrg || currentStepIndex !== -1) {
            return;
        }
        if (!lastSavedStep) {
            // do something else??
        }

        return goToStep(lastSavedStep, true)
    }, [supplierOrg, currentStepIndex])


    useMount(() => {
        // Check if generated_account_data exists and navigate to import wizard
        if (supplierOrg?.generated_account_data && !TB.isEmpty(supplierOrg.generated_account_data)) {
            // navigate('/supplier/import-profile', { replace: true });
        } else if (auth.supplierOrg() && (
            !auth.supplierOrg?.()?.hasCompletedSupplierProfile() //|| !auth.supplierOrg?.()?.total_supplied_talent_users
        )) {
            TB.alert('confirm', 'Complete Supplier Profile',
                'Fill out some basic profile information for your agency and invite talent resources to begin managing opportunities.',
                {title: 'Ok'}
            )
        }
    })

    useEffect(() => {
        setSupplierOrg(TB.cloneDeep(auth?.supplierOrg()));
        setStepSubmitDisabled(false)
    }, [currentStepIndex])

    const isReviewing = () => {
        return false
    }

    const saveAndExitInvalidForm = async () => {
        if (TB.AlertIsShowing) {
            await TB.event('Alert@hide');
            await TB.wait(400);
            console.log('saveAndExitInvalidForm', 'WAITING')
        }
        let exitAnyways = await TB.sysAlert('talent.application_save_and_exit_button_clicked');

        console.log('saveAndExitInvalidForm', {exitAnyways})
        if (exitAnyways) {
            return navigate('/supplier/dashboard', {replace: true})
        }
    }

    const goToStep = (step, replace = false) => {
        let route = {
            ...location,
            pathname: step.path
        }

        if (replace) {
            return navigate(route, {replace: true});
        }
        return navigate(route)
    }

    const toggleDisabled = (submitDisabled) => {
        return setStepSubmitDisabled(submitDisabled)
    }

    const onUpdate = async (modelProps) => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;
        let updates = {...supplierOrg}
        for (let [key, value] of Object.entries(modelProps)) {
            updates = TB.set(updates, key, value)
        }
        console.log('onUpdate', {updates, modelProps})
        return setSupplierOrg(Client.make(TB.cloneDeep(updates)));
    };

    const onSaveStep = async (modelProps, skipGoToNext = false, skipValidateForImport = false) => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;
        let updates = {...supplierOrg}
        for (let [key, value] of Object.entries(modelProps)) {
            updates = TB.set(updates, key, value)
        }
        return onSubmit(updates, false, skipGoToNext, skipValidateForImport)
    }

    const onSubmit = async (params, exitApplication = false, skipGoToNext = false, skipValidateForImport = false) => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;

        console.log('onSubmit', params);

        if (params) {
            if (!skipValidateForImport) {
                if (formRef?.current) {
                    console.log('onSubmit', formRef?.current)
                    let invalid = formRef.current.forceValidate();

                    if (invalid) {
                        const node = ReactDOM.findDOMNode(invalid);

                        return $('html, body').animate({scrollTop: $(node).offset().top - 50}, 800);
                    }
                }
                if (currentStep?.validateInputs) {
                    let validatedInputs = await currentStep.validateInputs(params);
                    if (!validatedInputs) {
                        return;
                    }
                }
            }
            await setIsDisabled(true)

            let updatedOrg;
            try {
                if (currentStep?.requestParams?.length > 0) {
                    let requestParams = TB.pick(params, [...DEFAULT_REQUEST_PARAMS, ...currentStep?.requestParams]);

                    let response = await updateSupplierManageProfileMutation.mutateAsync({...requestParams});

                    if (response) {
                        if (response.success.data.auth) {
                            await updateAuth({...response.success.data.auth});
                        }

                        updatedOrg = Client.make({...(response.success.data.client)});
                    }
                }else {
                    updatedOrg = supplierOrg;
                }

            } catch (e) {
                updatedOrg = false;
                console.warn('ERROR: onSubmit', e);

                if (TB.auth().SupplierUpdateRequest.didFail &&
                    (TB.auth().SupplierUpdateRequest.errorType === TB.Request.ErrorTooLong ||
                        TB.auth().SupplierUpdateRequest.errorType === TB.Request.ErrorLocal)) {

                    setIsDisabled(false)

                    return await TB.alert('error',
                        TB.trans('api.system_maintenance.title'),
                        TB.trans('api.system_maintenance.message', {code: 'XXX'}), [
                            {
                                title: 'Okay',
                            }
                        ]
                    )
                }
            }

            if (!updatedOrg) {
                /*
                * Update form with server errors
                * */
                console.log('onSubmit', TB.auth().SupplierUpdateRequest)
                if (await TB.auth().talentHasInputResponseError()) {
                    // await routeRef.current?.handleInputResponseError(TB.auth().SupplierUpdateRequest);
                }


                if (exitApplication) return saveAndExitInvalidForm();

                return setIsDisabled(false)
            }

            await setSupplierOrg(Client.make(updatedOrg));
        }


        if (exitApplication) {
            await setIsDisabled(false);

            return navigate('/supplier/dashboard', {replace: true})
        }

        if (skipGoToNext) {
            await setIsDisabled(false);
            TB.toast('Profile Updated');
            return;
        }

        if (nextStep) {
            if (currentStepIndex > activeSteps.indexOf(lastSavedStep.path)) {
                await setLastSavedStep(() => currentStep)
            }
            await setIsDisabled(false)

            return goToStep(nextStep);
        }

        await setIsDisabled(false)

        let redirect;

        if (!TB.auth().isAppropriate(location)) {
            redirect = appropriate();
        }

        if (redirect) {
            navigate(redirect)
        }

        if (currentStep === TB.last(activeSteps)) {
            await TB.alert('success', 'Profile Updated Successfully',
                'Your profile is now completed. You can edit this information at any time', [
                    {title: 'Go to dashboard', click: () => navigate('/supplier/dashboard')},
                    {
                        title: 'Continue editing', click: () => window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                        })
                    }
                ]
            );
        }
    }

    if (!currentStep) {
        return null
    }

    if (!profileCompleted && currentStep?.path !== "about") {
        return <Navigate to={'about'} replace/>;
    }

    // Fix for showing progress text when percent is small, i.e 10% round up to 15

    let isLastStep = currentStepIndex === activeSteps.length - 1,
        lastStepRemaining;// = supplierOrg.lastStepRemaining();

    let routeProps = {
        isDisabled: isDisabled || stepSubmitDisabled,
        isUpdating: updateSupplierManageProfileMutation.isLoading,
        supplierOrg,
        onEditItem: toggleDisabled,
        onChange: onUpdate,
        onSave: onSaveStep,
        isFormSubmitted: () => formRef?.current?.isFormSubmitted?.()
    }
    return (
        <>
            <Helmet>
                <title>{activeSteps[currentStepIndex]?.label} | Supplier | Wripple</title>
            </Helmet>

            <div className="talent-application-container supplier-application-container">
                <div className="talent-application-form-container">
                    <div className="talent-app-nav p-0 w-full lg:w-3/12 xl:w-2/12 relative" style={{borderTop: '1px solid #E5E5E5'}}>
                        <h3 className="font-bold">
                            Agency Profile
                        </h3>
                        {activeSteps.map(step => (
                            <button
                                key={step.path}
                                type="button"
                                className={TB.mapClass('step-title pointer-events-auto', {
                                    'isCompleted': !step.isDisabled && step.isCompleted,
                                    "inActive": step.isDisabled,
                                    'isActive': location.pathname.includes(step.path),
                                })}
                                disabled={step.isDisabled || updateSupplierManageProfileMutation.isLoading}
                                onClick={() => navigate(step.path)}
                            >
                                <i className={TB.mapClass("w-4 flex flex-col items-center justify-center text-[#90A0B7]", {
                                    "text-danger": step.needsCompletion,
                                    [step.icon]: true
                                })}/>

                                {step.label}
                            </button>
                        ))}

                        {/* AI Profile Ready Treatment */}
                        {hasAIProfileData && (
                            <div
                                className="w-full mt-4 text-white px-3 py-2 rounded-sm flex items-center gap-3 font-semibold text-xs animate-[aiProfilePulse_2s_ease-in-out_infinite]"
                                style={{
                                    background: secondaryColor,
                                }}
                            >
                                <i className="fas fa-sparkles text-sm whitespace-nowrap" />
                                <span>AI Profile Ready!</span>
                                <Link
                                    to="/supplier/import-profile/summary"
                                    className="flex flex-row gap-2 bg-white px-3 py-1 rounded-md font-semibold text-xs no-underline transition-transform duration-200 ease-in-out hover:scale-105 ml-auto"
                                    style={{
                                        color: secondaryColor,
                                    }}
                                >
                                    <span>Import</span><ArrowRightOutlined />
                                </Link>
                                <style>
                                    {`
                                        @keyframes aiProfilePulse {
                                            0%, 100% {
                                                transform: translateY(0);
                                            }
                                            50% {
                                                transform: translateY(-2px);
                                            }
                                        }
                                    `}
                                </style>
                            </div>
                        )}
                    </div>
                    <div className="w-full lg:w-9/12 xl:w-10/1">
                        <div className="talent-application px-4 md:px-8 xl:w-10/12 w-full mr-auto ml-0">

                            <Routes>
                                <Route index element={<Navigate to={'about'} replace/>}/>
                                <Route path="about"
                                    element={<SupplierInfoStep ref={formRef} {...routeProps}/>}/>
                                <Route path="brands"
                                       element={<SupplierBrandsStep {...routeProps}/>}/>
                                <Route path="work-gallery"
                                    element={<SupplierWorkGalleryStep {...routeProps}/>}/>
                                <Route path="project-highlights"
                                    element={<SupplierKeyClientsStep {...routeProps}/>}/>
                                <Route path="partner-programs"
                                    element={<SupplierPartnerProgramsStep {...routeProps}/>}/>
                                <Route path="industries"
                                    element={<SupplierIndustriesStep {...routeProps}/>}/>
                                <Route path="awards-certifications"
                                       element={<SupplierAwardsStep {...routeProps}/>}/>
                                <Route path="service-offerings/*"
                                       element={<ServiceOfferingsStep ref={formRef} {...routeProps}/>}/>
                                {/*<Route path="links-documents"*/}
                                {/*       element={<SupplierLinksDocumentsStep {...routeProps}/>}/>*/}
                                <Route path="talent-resources/*"
                                    element={<SupplierTalentResourcesStep {...routeProps}/>}/>

                                <Route path={'*'} element={<PageNotFound/>}/>

                            </Routes>

                        </div>
                    </div>
                </div>


                <div className="talent-app-footer">
                    {!location.pathname.includes('invite') && !location.pathname.includes('edit') && (
                        <React.Fragment>
                            <div className="lg:w-9/12 xl:w-10/12 ml-auto flex flex-row justify-end gap-4 items-center">
                                {(currentStepIndex > 0) && (
                                    <button
                                        type="button"
                                        onClick={() => goToStep(prevStep, true)}
                                        disabled={isDisabled || stepSubmitDisabled || currentStepIndex === 0}
                                        className="btn btn-outline-dark">
                                        {TB.trans('talent.talent_application.buttons.back')}
                                    </button>
                                )}


                                {profileCompleted && (
                                    <div className="save-and-exit">
                                        <button type="button"
                                                disabled={isDisabled || stepSubmitDisabled}
                                                onClick={() => onSubmit(supplierOrg, true)}
                                                className="btn btn-outline-dark">
                                            {TB.trans('talent.talent_app_step.buttons.save_exit')}
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    disabled={isDisabled || stepSubmitDisabled || (isLastStep && !auth.supplierOrg?.().total_supplied_talent_users)}
                                    onClick={() => onSubmit(supplierOrg)}
                                    className="btn btn-primary">
                                    {isLastStep ? 'Save' :
                                        'Save & Continue'
                                    }
                                </button>
                            </div>
                            {profileCompleted &&  (
                                <div className="md:w-full save-and-exit-mobile">
                                    <button type="button"
                                            disabled={isDisabled || stepSubmitDisabled}
                                            onClick={() => onSubmit(supplierOrg, true)}
                                            className="btn btn-link">
                                        {TB.trans('talent.talent_app_step.buttons.save_exit')}
                                    </button>
                                </div>
                            )}
                        </React.Fragment>
                    )}

                </div>
            </div>
        </>
    );
}

export default SupplierManageProfile